import React, { useMemo, useState, useEffect } from "react";
import * as THREE from "three";
import { useViewerStore } from "context/ViewerContext";

interface Bounds {
  min: number;
  max: number;
}

interface ClippingPanelProps {
  axis: "x" | "z";
  onClippingChange?: (planes: THREE.Plane[], bounds: Bounds) => void;
}

const AXIS_VECTOR: Record<"x" | "z", THREE.Vector3> = {
  x: new THREE.Vector3(-1, 0, 0),
  z: new THREE.Vector3(0, 0, -1),
};

function offsetFromPercent(bounds: Bounds, percent: number): number {
  const span = bounds.max - bounds.min;
  if (!Number.isFinite(span) || span === 0) return bounds.max;
  return bounds.min + (Math.max(0, Math.min(100, percent)) / 100) * span;
}

function percentFromOffset(bounds: Bounds, offset: number): number {
  const span = bounds.max - bounds.min;
  if (!Number.isFinite(span) || span === 0) return 100;
  return Math.round(((offset - bounds.min) / span) * 100);
}

const ClippingPanel: React.FC<ClippingPanelProps> = ({
  axis,
  onClippingChange,
}) => {
  const [bounds, setBounds] = useState<Bounds>({ min: 0, max: 10 });
  const percent = useViewerStore((state) =>
    axis === "z" ? state.clippingVerticalPercent : state.clippingLateralPercent
  );
  const [offset, setOffset] = useState(() => offsetFromPercent({ min: 0, max: 10 }, percent));

  useEffect(() => {
    const requestBounds = () => {
      const event = new CustomEvent("requestClippingBounds", {
        detail: {
          callback: (box: Record<string, number>) => {
            const minKey = `min${axis.toUpperCase()}`;
            const maxKey = `max${axis.toUpperCase()}`;
            const nextBounds = { min: box[minKey], max: box[maxKey] };
            const storePercent = axis === "z"
              ? useViewerStore.getState().clippingVerticalPercent
              : useViewerStore.getState().clippingLateralPercent;
            setBounds(nextBounds);
            setOffset(offsetFromPercent(nextBounds, storePercent));
          },
        },
      });
      document.dispatchEvent(event);
    };

    requestBounds();
    document.addEventListener("updateClippingBounds", requestBounds);
    return () => {
      document.removeEventListener("updateClippingBounds", requestBounds);
    };
  }, [axis]);

  useEffect(() => {
    setOffset(offsetFromPercent(bounds, percent));
  }, [bounds, percent]);

  useEffect(() => {
    return () => {
      const event = new CustomEvent("deactivateClipping", { detail: { axis } });
      document.dispatchEvent(event);
    };
  }, [axis]);

  const clippingPlanes = useMemo(() => {
    return [new THREE.Plane(AXIS_VECTOR[axis], offset)];
  }, [axis, offset]);

  useEffect(() => {
    if (onClippingChange) {
      onClippingChange(clippingPlanes, bounds);
    } else {
      const event = new CustomEvent("clippingPlanesChange", {
        detail: { planes: clippingPlanes, axis: axis },
      });
      document.dispatchEvent(event);
    }
  }, [clippingPlanes, bounds, onClippingChange]);

  return (
    <div className="clipping-panel">
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={0.1}
        value={offset}
        onChange={(e) => {
          const next = Number(e.target.value);
          setOffset(next);
          useViewerStore.getState().setClippingPercent(axis, percentFromOffset(bounds, next));
        }}
        className={`${axis.toUpperCase()}Slider`}
      />
    </div>
  );
};

export default ClippingPanel;
